import { CartAddEvent } from '@theme/events';

function parseVariantId(rawVariantId) {
  const normalized = rawVariantId?.trim();
  if (!normalized) return null;

  if (!/^\d+$/.test(normalized)) return null;

  const variantId = Number(normalized);
  if (!Number.isSafeInteger(variantId) || variantId <= 0) return null;

  return variantId;
}

function showButtonError(button, message) {
  if (!button.dataset.originalLabel) {
    button.dataset.originalLabel = button.textContent?.trim() || '';
  }

  button.textContent = message;
  button.classList.add('fcd-add-to-cart-button--error');

  window.setTimeout(() => {
    button.textContent = button.dataset.originalLabel || button.textContent;
    button.classList.remove('fcd-add-to-cart-button--error');
    button.disabled = false;
  }, 3000);
}

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : ''))
    .filter(Boolean)
    .join(',');
}

/**
 * @param {number} variantId
 */
async function addVariantToCart(variantId) {
  /** @type {{ items: Array<{ id: number, quantity: number }>, sections?: string, sections_url?: string }} */
  const payload = {
    items: [{ id: variantId, quantity: 1 }],
  };

  const sections = getCartSectionIds();
  if (sections) {
    payload.sections = sections;
    payload.sections_url = window.location.pathname;
  }

  const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  let data = null;

  try {
    data = await response.json();
  } catch (error) {
    throw new Error(`HTTP ${response.status}`);
  }

  if (!response.ok || data?.status) {
    const message = data?.description || data?.message || `HTTP ${response.status}`;
    throw new Error(message);
  }

  return data;
}

async function fetchCart() {
  const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) return null;

  return response.json();
}

/**
 * @param {HTMLElement} button
 * @param {number} variantId
 * @param {Record<string, unknown>} addResponse
 */
async function handleAddSuccess(button, variantId, addResponse) {
  const cart = await fetchCart();

  document.dispatchEvent(
    new CartAddEvent(cart ?? addResponse, button.id, {
      source: 'fcd-add-to-cart-button',
      itemCount: cart?.item_count ?? 1,
      productId: button.dataset.productId,
      variantId: String(variantId),
      sections: addResponse.sections,
    })
  );

  const redirectUrl = button.dataset.redirectUrl?.trim();
  if (redirectUrl) {
    window.location.href = redirectUrl;
    return;
  }

  const drawer = document.getElementById('site-cart-drawer');
  if (drawer && 'open' in drawer && typeof drawer.open === 'function') {
    drawer.open();
  }

  button.disabled = false;
}

function initFcdAddToCartButtons() {
  document.querySelectorAll('.fcd-add-to-cart-button[data-variant-id]').forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.dataset.fcdCartAddInit === 'true') return;

    const variantId = parseVariantId(button.dataset.variantId);
    if (!variantId) return;

    button.dataset.fcdCartAddInit = 'true';

    button.addEventListener('click', async (event) => {
      event.preventDefault();

      button.disabled = true;

      try {
        const data = await addVariantToCart(variantId);
        await handleAddSuccess(button, variantId, data);
      } catch (error) {
        const fallbackMessage = button.dataset.errorLabel?.trim() || 'Could not add to cart';
        const message = error instanceof Error && error.message ? error.message : fallbackMessage;

        console.error('[fcd-add-to-cart-button]', {
          variantId,
          productId: button.dataset.productId || null,
          error: message,
        });

        showButtonError(button, message);
      }
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFcdAddToCartButtons);
} else {
  initFcdAddToCartButtons();
}

document.addEventListener('shopify:section:load', initFcdAddToCartButtons);
